import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { PercursoStep, PercursoStop } from 'shared';
import { ACTIVITY_TYPE_LABELS, BADGE_UNIVERSO_COLORS } from 'shared';
import {
  formatDay,
  formatDuration,
  formatSpan,
  markerLane,
  stopMarker,
  stopPlaces,
  stopUniverso,
  trailPath,
  trailRows,
} from '@/lib/percurso';
import type { TrailPoint } from '@/lib/percurso';

/** Width of the column the trail winds down, in px. */
const GUTTER = 76;
/** Distance from a row's top to its marker's centre, in px. */
const MARKER_TOP = 22;

interface PercursoTrailProps {
  /** Oldest first, as the API sends them. */
  stops: ReadonlyArray<PercursoStop>;
  /** Who is reading: the participant, or a guardian looking at their educando. */
  forGuardian?: boolean;
}

/**
 * The percurso drawn as a trail: today at the top, the first day at the
 * bottom, one stop per day joined by a winding dotted line. The line is drawn
 * through the markers after layout, so it follows the cards whatever their
 * height.
 */
export default function PercursoTrail({ stops, forGuardian = false }: PercursoTrailProps) {
  const rows = useMemo(() => trailRows(stops), [stops]);
  const listRef = useRef<HTMLOListElement>(null);
  const [trail, setTrail] = useState<{ points: TrailPoint[]; height: number }>({
    points: [],
    height: 0,
  });

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    const measure = () => {
      const box = list.getBoundingClientRect();
      const points = Array.from(list.querySelectorAll<HTMLElement>('[data-trail-anchor]')).map(
        (anchor) => {
          const r = anchor.getBoundingClientRect();
          return { x: r.left + r.width / 2 - box.left, y: r.top + r.height / 2 - box.top };
        },
      );
      setTrail({ points, height: box.height });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [rows]);

  const path = trailPath(trail.points);
  let stopIndex = 0;

  return (
    <div className="relative">
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute left-0 top-0 w-full"
        style={{ height: trail.height }}
      >
        <path
          d={path}
          fill="none"
          stroke="hsl(var(--border))"
          strokeWidth={12}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d={path}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth={4}
          strokeLinecap="round"
          strokeDasharray="0.1 10"
        />
      </svg>

      <ol ref={listRef} className="relative space-y-4" aria-label="O seu percurso, do dia mais recente ao início">
        <HereRow forGuardian={forGuardian} />
        {rows.length === 0 && (
          <li style={{ paddingLeft: GUTTER }}>
            <div className="rounded-2xl border border-dashed border-border-strong bg-card p-4 text-sm text-muted-foreground">
              {forGuardian
                ? 'Ainda não há paragens. O percurso começa a desenhar-se aqui no primeiro check-in num Centro.'
                : 'Ainda não há paragens. Faça check-in num Centro, lendo o código QR à entrada, e o seu percurso começa a desenhar-se aqui.'}
            </div>
          </li>
        )}
        {rows.map((row) => {
          if (row.kind === 'month') {
            return (
              <li key={row.key} style={{ paddingLeft: GUTTER }} aria-hidden="true">
                <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  {row.label}
                </span>
              </li>
            );
          }
          const lane = markerLane(stopIndex);
          stopIndex += 1;
          return <StopRow key={row.key} stop={row.stop} lane={lane} />;
        })}
        <StartRow firstDay={stops[0]?.day ?? null} lane={markerLane(stopIndex)} />
      </ol>
    </div>
  );
}

// ─── Rows ────────────────────────────────────────────────────────────────────

function anchorStyle(lane: number): CSSProperties {
  return { left: lane, top: MARKER_TOP, transform: 'translate(-50%, -50%)' };
}

function HereRow({ forGuardian }: { forGuardian: boolean }) {
  return (
    <li className="relative min-h-[44px]" style={{ paddingLeft: GUTTER }}>
      <span data-trail-anchor className="absolute z-10 flex h-5 w-5" style={anchorStyle(36)}>
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-40" />
        <span className="relative inline-flex h-5 w-5 rounded-full bg-primary ring-4 ring-background" />
      </span>
      <p className="pt-2.5 text-sm font-black text-primary">{forGuardian ? 'Hoje' : 'Está aqui'}</p>
      <p className="text-xs text-muted-foreground">Cada dia no Centro acrescenta uma paragem.</p>
    </li>
  );
}

function StartRow({ firstDay, lane }: { firstDay: string | null; lane: number }) {
  return (
    <li className="relative min-h-[44px]" style={{ paddingLeft: GUTTER }}>
      <span
        data-trail-anchor
        className="absolute z-10 flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-secondary-foreground ring-4 ring-background"
        style={anchorStyle(lane)}
        aria-hidden="true"
      >
        <FlagGlyph />
      </span>
      <p className="pt-2.5 text-sm font-black text-foreground">Início do percurso</p>
      <p className="text-xs text-muted-foreground">
        {firstDay ? capitalise(formatDay(firstDay)) : 'O primeiro check-in num Centro começa aqui.'}
      </p>
    </li>
  );
}

function StopRow({ stop, lane }: { stop: PercursoStop; lane: number }) {
  const places = stopPlaces(stop);
  const visits = stop.steps.filter((s) => s.kind === 'visit');
  const others = stop.steps.filter((s) => s.kind !== 'visit');

  return (
    <li className="relative" style={{ paddingLeft: GUTTER }}>
      <StopMarkerDot stop={stop} lane={lane} />
      <article className="rounded-2xl border border-border bg-card p-3 shadow-sm">
        <header>
          <h2 className="text-sm font-black text-foreground">{capitalise(formatDay(stop.day))}</h2>
          {places.length > 0 && (
            <p className="truncate text-xs text-muted-foreground">
              {places.join(' · ')}
              {visits.length > 0 && ` · ${visits.map(formatSpan).join(', ')}`}
            </p>
          )}
        </header>
        {others.length === 0 ? (
          <p className="mt-2 text-xs text-muted-foreground">Passou pelo Centro.</p>
        ) : (
          <ul className="mt-2 space-y-2">
            {others.map((step) => (
              <StepLine key={`${step.kind}-${step.at}-${step.title}`} step={step} />
            ))}
          </ul>
        )}
      </article>
    </li>
  );
}

function StopMarkerDot({ stop, lane }: { stop: PercursoStop; lane: number }) {
  const marker = stopMarker(stop);
  const universo = stopUniverso(stop);
  const fill = universo ? BADGE_UNIVERSO_COLORS[universo].bg : undefined;
  const base = 'absolute z-10 flex items-center justify-center rounded-full ring-4 ring-background';

  if (marker === 'badge') {
    return (
      <span
        data-trail-anchor
        className={`${base} h-9 w-9 bg-secondary text-white`}
        style={{ ...anchorStyle(lane), backgroundColor: fill }}
        aria-hidden="true"
      >
        <StarGlyph />
      </span>
    );
  }
  if (marker === 'session') {
    return (
      <span
        data-trail-anchor
        className={`${base} h-7 w-7 bg-primary`}
        style={{ ...anchorStyle(lane), backgroundColor: fill }}
        aria-hidden="true"
      >
        <span className="h-2 w-2 rounded-full bg-white" />
      </span>
    );
  }
  if (marker === 'equipment') {
    return (
      <span
        data-trail-anchor
        className={`${base} h-7 w-7 border-2 border-brand-slate bg-card text-brand-slate`}
        style={anchorStyle(lane)}
        aria-hidden="true"
      >
        <EquipmentGlyph />
      </span>
    );
  }
  return (
    <span
      data-trail-anchor
      className={`${base} h-4 w-4 border-[3px] border-brand-slate bg-card`}
      style={anchorStyle(lane)}
      aria-hidden="true"
    />
  );
}

function StepLine({ step }: { step: PercursoStep }) {
  if (step.kind === 'badge') {
    return (
      <li className="flex items-start gap-2 rounded-xl bg-pending-soft px-2 py-1.5">
        <span className="mt-0.5 text-pending">
          <StarGlyph />
        </span>
        <p className="min-w-0 text-sm text-foreground">
          Conquistou o badge <span className="font-bold">{step.title}</span>
          {step.universo && <span className="text-muted-foreground"> · {step.universo}</span>}
        </p>
      </li>
    );
  }

  if (step.kind === 'session') {
    const details = [
      step.activityType ? ACTIVITY_TYPE_LABELS[step.activityType] : null,
      step.universo,
      formatSpan(step),
    ].filter(Boolean);
    return (
      <li className="flex items-start gap-2">
        <span
          className="mt-1 h-3 w-3 flex-shrink-0 rounded-[4px] bg-primary"
          style={step.universo ? { backgroundColor: BADGE_UNIVERSO_COLORS[step.universo].bg } : undefined}
          aria-hidden="true"
        />
        <div className="min-w-0">
          <p className="text-sm font-bold text-foreground">{step.title}</p>
          <p className="text-xs text-muted-foreground">{details.join(' · ')}</p>
        </div>
      </li>
    );
  }

  return (
    <li className="flex items-start gap-2">
      <span className="mt-0.5 text-brand-slate" aria-hidden="true">
        <EquipmentGlyph />
      </span>
      <div className="min-w-0">
        <p className="text-sm font-bold text-foreground">{step.title}</p>
        <p className="text-xs text-muted-foreground">
          {step.endedAt ? `Usou durante ${formatDuration(step.at, step.endedAt)}` : 'A usar agora'}
        </p>
      </div>
    </li>
  );
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// ─── Glyphs ──────────────────────────────────────────────────────────────────

function StarGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
      <path d="M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4l-5.9 3.1 1.2-6.5-4.8-4.6 6.6-.9z" />
    </svg>
  );
}

function FlagGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2.2} aria-hidden="true">
      <path d="M5 21V4" strokeLinecap="round" />
      <path d="M5 4h11l-2 4 2 4H5" strokeLinejoin="round" />
    </svg>
  );
}

function EquipmentGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <rect x="3" y="5" width="18" height="12" rx="2" strokeLinejoin="round" />
      <path d="M8 21h8" strokeLinecap="round" />
      <path d="M12 17v4" strokeLinecap="round" />
    </svg>
  );
}
